import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import { usePerms } from 'contexts/PermsContext';
import { useEffect, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { IoHeartOutline, IoHomeOutline, IoPersonOutline } from 'react-icons/io5';
import { getUserProfileInfo, updateUserProfile } from 'services/user/user.service';
import { UpdatePassword } from '../UpdatePassword';
import UserMenuPanel from './components/UserMenuPanel';
const isDev = process.env.NODE_ENV === 'development';

export const UserInfo = () => {
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [originalUserInfo, setOriginalUserInfo] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Permissions context
  const perms = usePerms();

  // Define required permission for viewing/updating user info
  const canViewUserInfo = perms?.testPermission
    ? perms.testPermission([8, 9, 10, 11, 12, 13], ['ViewUsers'])
    : false;

  const [inputUserInfo, setInputUserInfo] = useState({
    name: '',
    email: '',
    employeeId: '',
    phoneNumber: '',
    dob: '',
    gender: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipcode: '',
    },
    emergencyContact: {
      name: '',
      phone: '',
    },
  });

  const defaultAddress = {
    street: '',
    city: '',
    state: '',
    zipcode: '',
  };

  const defaultEmergencyContact = {
    name: '',
    phone: '',
  };

  const sanitizeString = (value) => (value === undefined || value === null ? '' : String(value));

  const normalizeDateForInput = (value) => {
    if (!value) return '';

    // Accept already-correct values
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

    // Handle MM/DD/YYYY or MM-DD-YYYY
    const match = value.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})$/);
    if (match) {
      let [, month, day, year] = match;
      if (year.length === 2) year = `20${year}`;
      const y = year.padStart(4, '0');
      const m = month.padStart(2, '0');
      const d = day.padStart(2, '0');
      return `${y}-${m}-${d}`;
    }

    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);

    return '';
  };

  const normalizeCompositeField = (value, fallback) => {
    const mergeFallback = (obj) => ({ ...fallback, ...(obj || {}) });

    if (!value) return mergeFallback();

    const parseJsonString = (input) => {
      if (!input) return null;
      try {
        const parsed = JSON.parse(input);
        if (typeof parsed === 'string') return parseJsonString(parsed);
        return parsed;
      } catch {
        if (isDev) console.warn('Failed to parse composite field:', input);
        return null;
      }
    };

    const buildStringFromNumericKeys = (obj) => {
      const numericKeys = Object.keys(obj).filter((key) => /^\d+$/.test(key));
      if (!numericKeys.length) return null;
      return numericKeys
        .sort((a, b) => Number(a) - Number(b))
        .map((key) => obj[key])
        .join('');
    };

    if (typeof value === 'string' || value instanceof String) {
      const parsed = parseJsonString(value.toString());
      return mergeFallback(parsed);
    }

    if (typeof value === 'object') {
      const serialized = buildStringFromNumericKeys(value);
      if (serialized) {
        const parsed = parseJsonString(serialized);
        return mergeFallback(parsed);
      }
      return mergeFallback(value);
    }

    return mergeFallback();
  };

  const formatEmergencyContactForDisplay = (contact = {}) => ({
    name: sanitizeString(contact?.name),
    phone: formatPhoneNumberForDisplay(sanitizeString(contact?.phone || '')),
  });

  const formatEmergencyContactForApi = (contact = {}) => ({
    ...contact,
    phone: formatPhoneNumberForApi(contact?.phone || ''),
  });

  const buildProfileFromResponse = (data = {}) => ({
    name: sanitizeString(data?.name),
    email: sanitizeString(data?.email),
    employeeId: sanitizeString(data?.employeeId),
    phoneNumber: formatPhoneNumberForDisplay(sanitizeString(data?.phoneNumber || '')),
    dob: normalizeDateForInput(data?.dob),
    gender: sanitizeString(data?.gender) || 'Not Specified',
    address: normalizeCompositeField(data?.address, defaultAddress),
    emergencyContact: formatEmergencyContactForDisplay(
      normalizeCompositeField(data?.emergencyContact, defaultEmergencyContact),
    ),
  });

  const cloneProfile = (profile) => JSON.parse(JSON.stringify(profile || {}));

  const profilesMatch = (a, b) => {
    if (!a || !b) return false;
    return JSON.stringify(a) === JSON.stringify(b);
  };

  const updateUserInfoState = (updater) => {
    setInputUserInfo((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      if (originalUserInfo) setHasChanges(!profilesMatch(next, originalUserInfo));
      else setHasChanges(true);
      return next;
    });
  };

  const formatDateForApi = (value) => {
    if (!value) return null;
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(value)) return value;
    const match = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (match) {
      const [, year, month, day] = match;
      return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
    }
    return value;
  };

  useEffect(() => {
    const fetchProfile = async () => {
      setIsLoading(true);
      setFetchError(null);
      try {
        if (!canViewUserInfo) {
          setFetchError('You do not have permission to view this information.');
          setIsLoading(false);
          return;
        }
        const res = await getUserProfileInfo();
        const profile = buildProfileFromResponse(res?.data?.result);
        setInputUserInfo(profile);
        setOriginalUserInfo(cloneProfile(profile));
        setHasChanges(false);
      } catch (err) {
        if (isDev) console.error('Failed to load user profile info', err);
        setFetchError('Failed to load account information. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [canViewUserInfo]);
  const handleSubmit = async () => {
    if (isLoading || isSaving || !hasChanges) return;

    if (!canViewUserInfo) {
      setError('You do not have permission to update this information.');
      return;
    }

    const payload = {
      phoneNumber: formatPhoneNumberForApi(inputUserInfo.phoneNumber || ''),
      gender: inputUserInfo.gender,
      birthday: formatDateForApi(inputUserInfo.dob),
      address: JSON.stringify(normalizeCompositeField(inputUserInfo.address, defaultAddress)),
      emergencyContact: JSON.stringify(
        formatEmergencyContactForApi(
          normalizeCompositeField(inputUserInfo.emergencyContact, defaultEmergencyContact),
        ),
      ),
    };

    setIsSaving(true);
    setError(null);
    setMessage(null);

    try {
      const res = await updateUserProfile(
        payload.phoneNumber,
        payload.gender,
        payload.birthday,
        payload.address,
        payload.emergencyContact,
      );

      if (res?.status === 200) {
        setMessage('Account information updated successfully');
        setError(null);
        setOriginalUserInfo(cloneProfile(inputUserInfo));
        setHasChanges(false);
      } else {
        const errMsg = res?.response?.data?.message || 'Unknown error occurred while saving';
        setError('Error updating user info: ' + errMsg);
        setMessage(null);
      }
    } catch (e) {
      const errMsg = e?.response?.data?.message || e?.message || 'Unknown error';
      setError('Error updating user info: ' + errMsg);
      setMessage(null);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading && !originalUserInfo) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  return (
    <div className="user-menu-page">
      {showChangePassword ? (
        <UpdatePassword setShowChangePassword={setShowChangePassword} />
      ) : (
        <>
          <UserMenuPanel icon={<IoPersonOutline />} title="Information">
            <div className="user-menu-field-grid">
              <InputItem
                controlId="account-name"
                editable={false}
                title="Name"
                value={inputUserInfo.name}
              />
              <InputItem
                controlId="account-email"
                editable={false}
                title="Email"
                value={inputUserInfo.email}
              />
              <InputItem
                controlId="account-employee-id"
                editable={false}
                title="Employee ID"
                value={inputUserInfo.employeeId}
              />
              <InputItem
                controlId="account-phone"
                editable={!isLoading}
                title="Phone Number"
                type="tel"
                value={inputUserInfo.phoneNumber}
                setValue={(value) => {
                  const formatted = formatPhoneNumberForDisplay(value);
                  updateUserInfoState((prev) => ({
                    ...prev,
                    phoneNumber: formatted,
                  }));
                }}
              />
              <InputItem
                controlId="account-birthday"
                editable={!isLoading}
                title="Birthday"
                type="date"
                value={inputUserInfo.dob}
                setValue={(value) => {
                  updateUserInfoState((prev) => ({
                    ...prev,
                    dob: sanitizeString(value),
                  }));
                }}
              />
              <InputItem
                controlId="account-gender"
                editable={!isLoading}
                title="Gender"
                inputType="select"
                options={['Female', 'Male', 'Not Specified']}
                value={inputUserInfo.gender || 'Not Specified'}
                setValue={(value) => {
                  updateUserInfoState((prev) => ({
                    ...prev,
                    gender: sanitizeString(value),
                  }));
                }}
              />
            </div>
          </UserMenuPanel>

          <div className="user-menu-grid" data-layout="halves">
            <UserMenuPanel icon={<IoHomeOutline />} title="Home Address">
              <div className="user-menu-field-grid">
                {[
                  ['street', 'Street'],
                  ['city', 'City / County'],
                  ['state', 'State'],
                  ['zipcode', 'Zipcode'],
                ].map(([key, title]) => (
                  <InputItem
                    key={key}
                    controlId={`account-address-${key}`}
                    editable={!isLoading}
                    title={title}
                    value={inputUserInfo.address?.[key]}
                    setValue={(value) => {
                      updateUserInfoState((prev) => ({
                        ...prev,
                        address: {
                          ...prev.address,
                          [key]: sanitizeString(value),
                        },
                      }));
                    }}
                  />
                ))}
              </div>
            </UserMenuPanel>

            <UserMenuPanel icon={<IoHeartOutline />} title="Emergency Contact">
              <div className="user-menu-field-grid">
                <InputItem
                  controlId="account-emergency-name"
                  editable={!isLoading}
                  title="Name"
                  value={inputUserInfo.emergencyContact?.name}
                  setValue={(value) => {
                    updateUserInfoState((prev) => ({
                      ...prev,
                      emergencyContact: {
                        ...prev.emergencyContact,
                        name: sanitizeString(value),
                      },
                    }));
                  }}
                />
                <InputItem
                  controlId="account-emergency-phone"
                  editable={!isLoading}
                  title="Phone"
                  type="tel"
                  value={inputUserInfo.emergencyContact?.phone}
                  setValue={(value) => {
                    const formatted = formatPhoneNumberForDisplay(value);
                    updateUserInfoState((prev) => ({
                      ...prev,
                      emergencyContact: {
                        ...prev.emergencyContact,
                        phone: formatted,
                      },
                    }));
                  }}
                />
              </div>
            </UserMenuPanel>
          </div>

          <div className="user-menu-panel user-menu-actions">
            <Button variant="secondary" onClick={() => setShowChangePassword(true)}>
              Change Password
            </Button>
            <div role="status" className="d-flex flex-column text-center">
              {fetchError && (
                <p className="user-menu-status" data-tone="error">
                  {fetchError}
                </p>
              )}
              {error && (
                <p className="user-menu-status" data-tone="error">
                  {error}
                </p>
              )}
              {message && (
                <p className="user-menu-status" data-tone="success">
                  {message}
                </p>
              )}
            </div>
            <Button
              variant="primary"
              disabled={isLoading || isSaving || !hasChanges}
              onClick={handleSubmit}
            >
              Save
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

/**
 * One labelled account field. The label is tied to its control, so screen readers and
 * `getByLabelText` both find it.
 */
export const InputItem = ({
  controlId,
  editable,
  title,
  value = '',
  setValue = () => {},
  type = 'text',
  inputType = 'input',
  options,
}) => {
  const selectOptions = (options ?? []).map((option) => ({ value: option, label: option }));

  return (
    <Form.Group className="user-menu-field" controlId={controlId}>
      <Form.Label htmlFor={controlId}>{title}</Form.Label>
      {inputType === 'input' && (
        <Form.Control
          value={value ?? ''}
          type={type}
          disabled={!editable}
          onChange={(e) => setValue(e.target.value)}
        />
      )}
      {inputType === 'select' && (
        <BaseSelect
          inputId={controlId}
          isSearchable
          selectProps={{
            options: selectOptions,
            value: findSelectOption(selectOptions, value),
            isDisabled: !editable,
            onChange: (option) => setValue(option?.value ?? ''),
          }}
        />
      )}
    </Form.Group>
  );
};
const formatPhoneNumberForDisplay = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const formatPhoneNumberForApi = (value) => value.replace(/\D/g, '').slice(0, 10);
